import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  bulkConfirmDealerListings,
  fetchDealerInventoryQuality,
  runDealerListingAction,
} from "../../services/automapDealerInventoryApi";
import { fetchDealerVehicleRiskInsights } from "../../services/automapVehicleRiskApi";
import { RiskLevelPill } from "../../components/automap/RiskBadge";
import {
  formatListingStatus,
  qualityBadgeClass,
  trackInventoryEvent,
} from "../../constants/automapDealerInventoryConstants";
import { QUALITY_ISSUE_LABELS } from "../../constants/automapInventoryQualityConstants";

function suggestedAction(listing) {
  if (listing.qualityIssues?.includes("stale_confirmation")) return "Confirmar disponible";
  if (listing.qualityIssues?.includes("missing_photos")) return "Agregar fotos";
  if (listing.qualityScore < 70) return "Completar datos";
  return "Mantener actualizado";
}

export default function DealerInventoryQualityPage() {
  const { dealerId } = useOutletContext();
  const [summary, setSummary] = useState(null);
  const [riskMap, setRiskMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    fetchDealerInventoryQuality(dealerId)
      .then(setSummary)
      .finally(() => setLoading(false));
    fetchDealerVehicleRiskInsights(dealerId)
      .then((data) => {
        const map = Object.fromEntries((data.insights || []).map((i) => [i.listingId, i]));
        setRiskMap(map);
      })
      .catch(() => setRiskMap({}));
  };

  useEffect(() => {
    trackInventoryEvent("inventory_quality_viewed", { dealerId });
    load();
  }, [dealerId]);

  const staleListings = useMemo(
    () => (summary?.listings || []).filter((l) => l.qualityIssues?.includes("stale_confirmation") && l.status === "active"),
    [summary]
  );

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkConfirm = async () => {
    setBusy(true);
    try {
      await bulkConfirmDealerListings(dealerId, [...selected]);
      trackInventoryEvent("dealer_confirmed_available", { dealerId, count: selected.size });
      setSelected(new Set());
      load();
    } finally {
      setBusy(false);
    }
  };

  const confirmOne = async (listingId) => {
    await runDealerListingAction(dealerId, listingId, "confirm_available");
    trackInventoryEvent("dealer_confirmed_available", { listingId, dealerId });
    load();
  };

  if (loading) return <p className="dealer-state-box">Cargando calidad…</p>;
  if (!summary) return <p className="dealer-state-box">No hay datos de calidad.</p>;

  return (
    <div>
      <h2>Calidad del inventario</h2>
      <p className="muted">
        Mejora estos vehículos para recibir compradores más calificados y mantener buena visibilidad en AutoMapa.
      </p>

      <div className="dealer-form-grid">
        <p>Promedio calidad: <strong>{summary.averageScore}/100</strong></p>
        <p>Activos: {summary.active}</p>
        <p>Obsoletos: {summary.stale ?? summary.needsConfirmation}</p>
        <p>Sin fotos: {summary.missingPhotos ?? 0}</p>
        <p>Datos faltantes: {summary.missingRequired ?? 0}</p>
        <p>Pendientes confirmación: {summary.needsConfirmation}</p>
      </div>

      {(summary.topActions || []).length ? (
        <div className="dealer-inventory-toolbar">
          <strong>Prioridades:</strong>
          {summary.topActions.map((a) => (
            <span key={a.action} className="admin-badge admin-badge--info">{a.label} ({a.count})</span>
          ))}
        </div>
      ) : null}

      {staleListings.length > 0 ? (
        <div className="dealer-inventory-toolbar">
          <button type="button" className="dealer-portal-btn primary" disabled={busy || selected.size === 0} onClick={bulkConfirm}>
            Confirmar seleccionados ({selected.size})
          </button>
          <button type="button" className="dealer-portal-btn" disabled={busy} onClick={() => setSelected(new Set(staleListings.map((l) => l.id)))}>
            Seleccionar obsoletos
          </button>
        </div>
      ) : null}

      <table className="dealer-inventory-table">
        <thead>
          <tr>
            <th></th>
            <th>Vehículo</th>
            <th>Estado</th>
            <th>Calidad</th>
            <th>Riesgo</th>
            <th>Issues</th>
            <th>Confirmado</th>
            <th>Sugerencia</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(summary.listings || []).map((listing) => (
            <tr key={listing.id}>
              <td>
                {listing.status === "active" ? (
                  <input type="checkbox" checked={selected.has(listing.id)} onChange={() => toggleSelect(listing.id)} />
                ) : null}
              </td>
              <td>{[listing.make, listing.model, listing.year].filter(Boolean).join(" ")}</td>
              <td>{formatListingStatus(listing.status)}</td>
              <td>
                <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>
                  {listing.qualityScore} · {listing.qualityLabel}
                </span>
              </td>
              <td>
                {riskMap[listing.id] ? (
                  <RiskLevelPill riskLevel={riskMap[listing.id].riskLevel} />
                ) : (
                  "—"
                )}
              </td>
              <td>
                {(listing.qualityIssues || []).slice(0, 3).map((issue) => (
                  <span key={issue} className="admin-badge admin-badge--warning" style={{ marginRight: 4 }}>
                    {QUALITY_ISSUE_LABELS[issue] || issue}
                  </span>
                ))}
              </td>
              <td>{listing.lastConfirmedAt ? new Date(listing.lastConfirmedAt).toLocaleDateString("es-CO") : "—"}</td>
              <td>{suggestedAction(listing)}</td>
              <td className="dealer-inventory-actions">
                <Link to={`/v/automap/dealer/inventario/${listing.id}/editar`}>Editar</Link>
                {listing.status === "active" ? (
                  <>
                    <button type="button" onClick={() => confirmOne(listing.id)}>Confirmar</button>
                    <button type="button" onClick={() => runDealerListingAction(dealerId, listing.id, "mark_sold").then(load)}>Vendido</button>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
